/**
 * blog-recipes.js
 *
 * Client-side filtering, search and sort for the recipe listing page.
 *
 * How it works:
 * - The section renders full recipe cards in <li data-handle data-tags> items.
 * - On load, the remaining paginated pages are fetched once in the background
 *   and every card is cached in memory.
 * - Filtering matches selected tag slugs against each item's data-tags list
 *   (OR logic across selections) and re-renders instantly. No page reloads,
 *   so checkbox state and open accordions persist naturally.
 * - State is mirrored to the URL (?tags=a,b&diet=x,y&q=query) via replaceState
 *   so filtered views survive refresh and can be shared. Legacy /tagged/ URLs
 *   are also understood on load.
 * - The filters render twice (desktop sidebar + mobile drawer) with distinct
 *   id prefixes; changes are mirrored between the two copies by input name.
 *
 * ## Two kinds of filter, and they combine differently
 *
 * Category filters (`tags=`) UNION with each other: Fruit + Vegetables shows
 * fruit *or* vegetables, which is MORE recipes.
 *
 * Dietary filters (`diet=`) INTERSECT: Vegan shows FEWER, by removing anything
 * containing an animal product from whatever is already on screen. They also
 * intersect with the category selection, so Fruit + Vegan means fruit recipes
 * that are also vegan — not "fruit or vegan".
 *
 * Getting this wrong is the obvious bug: run Dietary through the same .some()
 * as the categories and ticking Vegan would ADD recipes to a Fruit selection.
 *
 * A dietary tag means "nothing in the ingredients rules this out", not "this is
 * certified vegan", so the filter only ever hides what was positively excluded.
 * An unrecognised ingredient leaves a recipe visible rather than wrongly
 * certified, which is the safe direction for the error to fall.
 */

/**
 * The dietary filters.
 *
 * `hides` is the label's subtitle, and it is phrased as what the filter REMOVES rather than what the
 * recipe IS. A recipe carries `dietary/vegan` when nothing in its ingredient list rules it out — not
 * because anyone certified it. "Hides recipes containing meat" is a claim about the filter and is
 * true; "Vegetarian recipes" is a claim about the food and this data cannot support it. Gelatine
 * hides in marshmallows, rennet in cheese, anchovy in Worcestershire sauce, and someone avoiding
 * those is relying on this being honest about what it knows.
 */
const DIETS = [
  { id: 'vegetarian', label: 'Vegetarian', hides: 'Hides recipes containing meat, poultry, seafood or gelatine' },
  { id: 'vegan', label: 'Vegan', hides: 'Hides recipes containing any animal product, including dairy, egg and honey' },
  { id: 'paleo', label: 'Paleo', hides: 'Hides recipes containing grains, legumes, dairy or refined sugar' },
  { id: 'carnivore', label: 'Carnivore', hides: 'Shows only recipes made entirely from animal products' },
  { id: 'gluten-free', label: 'Gluten Free', hides: 'Hides recipes containing wheat, barley, rye or malt' },
  { id: 'dairy-free', label: 'Dairy Free', hides: 'Hides recipes containing milk, cheese, butter or cream' },
  { id: 'keto', label: 'Keto', hides: 'Hides recipes containing sugar, grains or high-sugar fruit' },
  { id: 'raw', label: 'Raw', hides: 'Hides recipes dried above 46 °C' },
];

const DIET_LABELS = Object.fromEntries(DIETS.map((d) => [d.id, d.label]));

/**
 * Where the category tree comes from.
 *
 * The sidebar used to be hand-written in two places — ~57KB of Liquid and a matching set of
 * section blocks — and neither was connected to the categories collection. That is why
 * `Dairy > Dairy` outlived its collapse in the database by weeks, and why the tree and the article
 * tags drifted apart in the first place. This endpoint is that collection, so there is now one
 * definition and the storefront renders whatever it is given.
 *
 * Overridable with `data-filters-url` on the element, for testing against a preview deployment.
 */
const FILTERS_ENDPOINT = 'https://benchfoods-recipes.onrender.com/api/public/filters';

/** How long to wait before giving up and leaving the server-rendered sidebar in place. */
const FILTERS_TIMEOUT_MS = 6000;

const CHEVRON = '<svg width="12" height="8" viewBox="0 0 12 8" fill="none" aria-hidden="true"><path d="M1 1.5 6 6.5 11 1.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

const DIETARY_STYLES = `
  .recipe-dietary { display: grid; gap: 2px; margin: 0 0 8px; padding: 0; list-style: none; }
  .recipe-dietary__item { display: flex; align-items: center; padding: 5px 0; }
  .recipe-dietary__item label { cursor: pointer; }
  .recipe-dietary__note { margin: 0 0 12px; font-size: 1.2rem; line-height: 1.4; color: #666; }
  .recipe-dietary__divider { border: none; border-top: 1px solid #e0e0e0; margin: 0 0 4px; }
  /* Dietary chips subtract where the category chips add, so they are marked
     apart rather than sitting in the same orange run as the categories. */
  .recipe-filter-tag--diet { background: #1a1a1a; text-transform: none; }
`;

class BlogRecipes extends HTMLElement {
  constructor() {
    super();

    this.filterDrawer = this.querySelector('#recipe-filter-drawer');
    this.filterBtn = this.querySelector('.js-open-filter');
    this.sortBtn = this.querySelector('.js-blog-sort');
    this.filterForm = document.getElementById('recipe-filters-desktop') || document.forms['recipe-filters'];
    this.results = this.querySelector('blog-results');
    this.grid = this.results ? this.results.querySelector('ul') : null;
    this.paginationMessage = this.querySelector('.js-pagination-message');
    this.originalCountText = this.paginationMessage ? this.paginationMessage.textContent : '';

    // Cards rendered on this page load (page 1, or a legacy /tagged/ page)
    this.initialItems = this.grid ? Array.from(this.grid.querySelectorAll('li[data-handle]')) : [];
    // Full card cache across all pages — populated by loadAllArticles()
    this.allItems = null;
    // handle -> original position, for restoring "Most Recent" order
    this.orderIndex = new Map();
    this.initialItems.forEach((li, i) => this.orderIndex.set(li.dataset.handle, i));

    this.searchQuery = '';
    this._searchTimer = null;

    // tag -> the category's name, for chips. A tag is a path (`fruit-misc-fruits-apple`) and
    // reads badly on a chip; the name is what the customer ticked.
    this.tagLabels = new Map();
  }

  connectedCallback() {
    // Before addEventListeners, so the injected inputs are picked up by the same
    // querySelectorAll that wires the category checkboxes.
    this.renderDietaryFilters();
    this.addEventListeners();
    this.restoreFilterState();
    // The categories arrive over the network, so they land after the first paint. Until they do —
    // and for good, if the request fails — the server-rendered sidebar is what the visitor sees and
    // uses, which is why this replaces it rather than waiting for it.
    this.renderCategoryFilters().then((replaced) => {
      if (replaced) this.restoreFilterState();
    });
    this.loadAllArticles().then(() => {
      if (this.hasActiveFilters()) {
        this.applyFilters({ updateUrl: false });
      }
    });
  }

  hasActiveFilters() {
    return this.getSelectedTags().length > 0 || this.getSelectedDiets().length > 0 || this.searchQuery !== '';
  }

  // ── Dietary group ─────────────────────────────────────────────────────────

  /**
   * Renders the Dietary group into both filter copies.
   *
   * Built here rather than in Liquid on purpose. The category filters are ~57KB of hand-written
   * markup — nine near-identical blocks plus 178 leaves — and the section that renders them carries
   * an inlined base64 image; editing either to add one more group risks far more than it gains.
   * Everything else the user sees in this sidebar (the chips, the count, the empty state) is already
   * rendered by this file, so this is consistent with how the feature works rather than an exception
   * to it.
   */
  renderDietaryFilters() {
    const targets = [
      document.getElementById('recipe-filters-desktop'),
      // The drawer's filters are not inside a form, matching how the category copy is rendered.
      document.querySelector('#recipe-filter-drawer .drawer__content'),
    ].filter(Boolean);

    if (!targets.length || document.querySelector('.recipe-dietary')) return;

    const style = document.createElement('style');
    style.textContent = DIETARY_STYLES;
    document.head.appendChild(style);

    targets.forEach((target, i) => {
      // Distinct ids per copy, matching the `drawer-` convention the Liquid uses.
      const prefix = i === 0 ? '' : 'drawer-';
      const wrap = document.createElement('div');
      // Closed on load. Eight diets is a taller block than the category it sits above, and open by
      // default it pushed the categories — the thing most people came to browse — below the fold.
      wrap.innerHTML = `
        <details class="disclosure disclosure--row">
          <summary style="padding: calc(4 * var(--space-unit)) 0">
            <div class="flex justify-between items-center">
              <span class="disclosure__title h6 flex flex-auto">Dietary Prefilter</span>
              <span class="disclosure__toggle">
                <svg width="12" height="8" viewBox="0 0 12 8" fill="none" aria-hidden="true"><path d="M1 1.5 6 6.5 11 1.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </span>
            </div>
          </summary>
          <div class="disclosure__panel ml-7 has-motion">
            <div class="disclosure__content">
              <p class="recipe-dietary__note">Narrows whatever else you have selected. Always check the ingredients yourself.</p>
              <ul class="recipe-dietary" role="list">
                ${DIETS.map(
                  (d) => `<li class="recipe-dietary__item">
                    <input type="checkbox" name="diet=${d.id}" id="${prefix}diet-${d.id}" class="checkbox checkbox--small mr-2" title="${d.hides}">
                    <label for="${prefix}diet-${d.id}" class="label" title="${d.hides}">${d.label}</label>
                  </li>`
                ).join('')}
              </ul>
            </div>
          </div>
        </details>
        <hr class="recipe-dietary__divider">`;
      target.prepend(...wrap.childNodes);
    });
  }

  // ── Category filters, from the database ───────────────────────────────────

  /**
   * The two copies of the sidebar: the desktop form and the mobile drawer.
   *
   * Both are rendered from the same Liquid with different id prefixes, so anything injected has to
   * go into both or the drawer silently keeps the old tree.
   */
  filterCopies() {
    return [
      document.getElementById('recipe-filters-desktop'),
      document.querySelector('#recipe-filter-drawer .drawer__content'),
    ].filter(Boolean);
  }

  /**
   * The category icons already on the page, keyed by the label they sit next to.
   *
   * A fallback now rather than the source. The icons were lifted out of the Liquid and stored on
   * their categories, so the payload carries each one with the category it belongs to; this reads
   * whatever the rendered page still has, which keeps the sidebar looking right on a theme that has
   * not been updated, and on any category whose icon has not been stored yet.
   */
  harvestIcons(copy) {
    const icons = new Map();
    copy.querySelectorAll('.rfc__details').forEach((group) => {
      const label = group.querySelector('.rfc__label');
      const icon = group.querySelector('.rfc__icon');
      if (label && icon) icons.set(label.textContent.trim().toLowerCase(), icon.outerHTML);
    });
    return icons;
  }

  /**
   * The value a checkbox filters by.
   *
   * `tag` is the Shopify tag as written — `fruit/misc-fruits/apple` — while the cards carry
   * `data-tags` after Liquid's handleize, which turns the slashes into hyphens. Matching happens
   * against the card, so the card's spelling is the one to use.
   */
  tagValue(node) {
    return String(node.tag || '').replace(/\//g, '-');
  }

  categoryMarkup(node, id, icons, depth) {
    const value = this.tagValue(node);
    if (!value) return '';

    const label = this.escapeHtml(node.name);
    const children = Array.isArray(node.children) ? node.children : [];
    const has = children.length > 0;
    const box = `<input type="checkbox" name="tags=${value}" id="${id}"`;

    this.tagLabels.set(value, node.name);

    if (depth === 0) {
      // The category's own icon first; the one still on the page only if it has none.
      const stored = typeof node.icon === 'string' ? node.icon.trim() : '';
      const icon = stored
        ? `<span class="rfc__icon">${stored}</span>`
        : icons.get(String(node.name).trim().toLowerCase()) || '';
      const panel = has
        ? `<div class="rfc__panel">${children
            .map((child, i) => this.categoryMarkup(child, `${id}-${i + 1}`, icons, 1))
            .join('')}</div>`
        : '';
      return `<div class="rfc__details">
        <div class="rfc__row">
          ${box} class="checkbox checkbox--small rfc__parent-check">
          <label for="${id}" class="label rfc__check-label" aria-hidden="true"></label>
          ${icon}
          <label for="${id}" class="rfc__label" onclick="event.preventDefault();">${label}</label>
          ${has ? `<button type="button" class="rfc__toggle" aria-label="Toggle ${label}">${CHEVRON}</button>` : ''}
        </div>
        ${panel}
      </div>`;
    }

    if (depth === 1 && has) {
      return `<details class="disclosure">
        <summary style="padding: calc(2 * var(--space-unit)) 0">
          <div class="flex justify-between items-center">
            <span class="disclosure__title h6 flex flex-auto">
              ${box} class="checkbox checkbox--small mr-2">
              <label for="${id}" class="label" style="font-weight: 500">${label}</label>
            </span>
            <span class="disclosure__toggle">${CHEVRON}</span>
          </div>
        </summary>
        <div class="disclosure__panel ml-7">
          <ul class="list-none mt-2">${children
            .map((child, i) => this.categoryMarkup(child, `${id}-${i + 1}`, icons, 2))
            .join('')}</ul>
        </div>
      </details>`;
    }

    // A second-level category with nothing under it. Same weight as one that opens, so the column
    // does not look like two different kinds of thing.
    if (depth === 1) {
      return `<div class="disclosure rfc__leaf">
        <div class="flex items-center" style="padding: calc(2 * var(--space-unit)) 0">
          <span class="h6 flex flex-auto" style="margin-bottom: 0;">
            ${box} class="checkbox checkbox--small mr-2">
            <label for="${id}" class="label" style="font-weight: 500">${label}</label>
          </span>
        </div>
      </div>`;
    }

    // Third level and deeper. Nothing is deeper than three today; the nested list is there so a
    // fourth level added in the admin app renders instead of vanishing.
    const nested = has
      ? `<ul class="list-none ml-7">${children
          .map((child, i) => this.categoryMarkup(child, `${id}-${i + 1}`, icons, depth + 1))
          .join('')}</ul>`
      : '';
    return `<li class="h6">
      ${box} class="checkbox checkbox--small mr-2">
      <label for="${id}" class="label" style="font-weight: 400">${label}</label>
      ${nested}
    </li>`;
  }

  /**
   * Wires one generated group up the way the Liquid's own inline script wires the hand-written
   * ones: the chevron toggles, and anything ticked inside opens it.
   *
   * The filtering listener has to be added here too. `addEventListeners` runs before the fetch
   * resolves, so these inputs were not in the document when it did its querySelectorAll.
   */
  bindCategoryGroup(group) {
    const toggle = group.querySelector('.rfc__toggle');
    if (toggle) toggle.addEventListener('click', () => group.classList.toggle('is-open'));

    group.querySelectorAll('input[type="checkbox"]').forEach((checkbox) => {
      checkbox.addEventListener('change', (event) => {
        if (event.target.checked) group.classList.add('is-open');
        this.onCheckboxChange(event.target);
      });
    });

    if (group.querySelector('input[type="checkbox"]:checked')) group.classList.add('is-open');
  }

  /**
   * Replaces the hand-written category tree with the one from the database.
   *
   * Returns whether it replaced anything, so the caller knows whether the checked state has to be
   * restored onto new inputs. Every failure path leaves the page exactly as it was: an endpoint
   * that is down, slow, or returns something unexpected degrades to the sidebar the theme already
   * rendered, which is the status quo rather than an empty column.
   */
  async renderCategoryFilters() {
    const copies = this.filterCopies();
    if (!copies.length) return false;

    let payload;
    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), FILTERS_TIMEOUT_MS);
      const response = await fetch(this.dataset.filtersUrl || FILTERS_ENDPOINT, {
        signal: controller.signal,
      });
      clearTimeout(timer);
      if (!response.ok) throw new Error(`filters responded ${response.status}`);
      payload = await response.json();
    } catch (error) {
      console.warn('Recipe filters: keeping the theme sidebar.', error);
      return false;
    }

    const categories = payload && Array.isArray(payload.categories) ? payload.categories : [];
    // An empty tree is treated as a failure rather than obeyed. It would mean a database problem,
    // and emptying the sidebar in response to one helps nobody.
    if (!categories.length) {
      console.warn('Recipe filters: endpoint returned no categories; keeping the theme sidebar.');
      return false;
    }

    copies.forEach((copy, i) => {
      // The drawer's ids carry the prefix the Liquid uses, so the two copies never collide.
      const prefix = i === 0 ? '' : 'drawer-';
      const icons = this.harvestIcons(copy);
      const existing = Array.from(copy.querySelectorAll('.rfc__details'));

      const holder = document.createElement('div');
      holder.innerHTML = categories
        .map((node, n) => this.categoryMarkup(node, `${prefix}cat-${n + 1}`, icons, 0))
        .join('');
      const groups = Array.from(holder.children);

      if (existing.length) {
        existing[0].before(...groups);
        existing.forEach((group) => group.remove());
      } else {
        copy.append(...groups);
      }

      groups.forEach((group) => this.bindCategoryGroup(group));
    });

    return true;
  }

  // ── Event wiring ──────────────────────────────────────────────────────────

  addEventListeners() {
    if (this.filterBtn && this.filterDrawer) {
      this.filterBtn.addEventListener('click', () => this.filterDrawer.open());
    }

    if (this.sortBtn) {
      this.sortBtn.addEventListener('change', (event) => {
        const { detail: { selectedValue } } = event;
        this.handleSort(selectedValue);
      });
    }

    // Search — desktop sidebar form and mobile form
    const searchForms = document.querySelectorAll('form[name="recipe-search"], form[name="recipe-search-mobile"]');
    searchForms.forEach((form) => {
      const searchInput = form.querySelector('input[name="recipe-query"]');

      form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.handleSearch(searchInput ? searchInput.value : '');
      });

      if (searchInput) {
        searchInput.addEventListener('input', () => {
          clearTimeout(this._searchTimer);
          this._searchTimer = setTimeout(() => {
            this.handleSearch(searchInput.value);
          }, 300);
        });

        searchInput.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') {
            searchInput.value = '';
            this.handleSearch('');
          }
        });
      }
    });

    // Filter checkboxes — BOTH copies (desktop sidebar and mobile drawer).
    // Dietary inputs live in the same containers and are handled by the same
    // listener: the mirroring and re-filter are identical, and only the
    // matching rule in applyFilters() differs.
    const checkboxes = document.querySelectorAll(
      '#recipe-filters-desktop input[type="checkbox"], #recipe-filter-drawer input[type="checkbox"]'
    );
    checkboxes.forEach((checkbox) => {
      checkbox.addEventListener('change', (event) => this.onCheckboxChange(event.target));
    });

    // Delegated clicks: chip removal + clear-all in the empty state
    this.addEventListener('click', (event) => {
      const removeBtn = event.target.closest('.js-remove-tag');
      if (removeBtn) {
        // `data-name` carries the full input name. `data-tag` is the older
        // attribute the section used to render; kept as a fallback so a chip
        // still works if the two files are ever deployed out of step.
        const name = removeBtn.dataset.name || (removeBtn.dataset.tag ? `tags=${removeBtn.dataset.tag}` : '');
        this.removeFilter(name);
        return;
      }
      const clearBtn = event.target.closest('.js-clear-filters');
      if (clearBtn) {
        event.preventDefault();
        this.clearAll();
      }
    });
  }

  // ── Checkbox hierarchy + drawer/desktop mirroring ─────────────────────────

  onCheckboxChange(initiator) {
    // Mirror to every input with the same name (the other rendered copy),
    // then cascade the hierarchy within each copy independently.
    document.querySelectorAll(`input[name="${initiator.name}"]`).forEach((copy) => {
      copy.checked = initiator.checked;
      copy.indeterminate = false;
      this.updateChildren(copy);
      this.updateAncestors(copy);
    });
    this.applyFilters();
  }

  updateChildren(initiator) {
    const children = document.querySelectorAll(`[id^="${initiator.id}-"]`);
    children.forEach((child) => {
      child.checked = initiator.checked;
      child.indeterminate = false;
    });
  }

  updateAncestors(element) {
    while (element) {
      const parentId = element.id.split('-').slice(0, -1).join('-');
      const parent = parentId ? document.getElementById(parentId) : null;
      if (!parent || parent.type !== 'checkbox') return;

      const siblings = document.querySelectorAll(`[id^="${parent.id}-"]`);
      const unchecked = Array.from(siblings).filter((s) => !s.checked);

      parent.indeterminate = unchecked.length > 0 && unchecked.length < siblings.length;
      parent.checked = unchecked.length === 0;

      element = parent;
    }
  }

  // ── State ─────────────────────────────────────────────────────────────────

  getSelectedTags() {
    const scope = this.filterForm || document;
    const tags = new Set();
    scope.querySelectorAll('input[type="checkbox"]:checked').forEach((cb) => {
      if (cb.name && cb.name.startsWith('tags=')) {
        tags.add(cb.name.slice(5).trim());
      }
    });
    return Array.from(tags);
  }

  /** The dietary ids currently ticked. Separate from the tags because they combine with AND. */
  getSelectedDiets() {
    const scope = this.filterForm || document;
    const diets = new Set();
    scope.querySelectorAll('input[type="checkbox"]:checked').forEach((cb) => {
      if (cb.name && cb.name.startsWith('diet=')) {
        diets.add(cb.name.slice(5).trim());
      }
    });
    return Array.from(diets);
  }

  getStateFromURL() {
    const url = new URL(window.location.href);
    let tags = [];
    const param = url.searchParams.get('tags');
    const taggedMatch = window.location.pathname.match(/\/tagged\/([^/?#]+)/);
    if (param) {
      tags = param.split(',').filter(Boolean);
    } else if (taggedMatch) {
      tags = decodeURIComponent(taggedMatch[1]).split('+').filter(Boolean);
    }
    const dietParam = url.searchParams.get('diet');
    return {
      tags,
      diets: dietParam ? dietParam.split(',').filter(Boolean) : [],
      q: url.searchParams.get('q') || '',
    };
  }

  restoreFilterState() {
    const { tags, diets, q } = this.getStateFromURL();

    tags.forEach((tag) => {
      document.querySelectorAll(`input[name="tags=${tag}"]`).forEach((cb) => {
        cb.checked = true;
        this.updateAncestors(cb);
      });
    });

    diets.forEach((diet) => {
      document.querySelectorAll(`input[name="diet=${diet}"]`).forEach((cb) => {
        cb.checked = true;
      });
    });

    if (q) {
      this.searchQuery = q;
      document.querySelectorAll('input[name="recipe-query"]').forEach((input) => {
        input.value = q;
      });
    }

    if (tags.length || diets.length || q) {
      this.applyFilters({ updateUrl: false });
    }
  }

  updateURL(tags, diets, q) {
    const base = window.location.pathname.split('/tagged')[0];
    const url = new URL(window.location.origin + base);
    if (tags.length) url.searchParams.set('tags', tags.join(','));
    if (diets.length) url.searchParams.set('diet', diets.join(','));
    if (q) url.searchParams.set('q', q);
    window.history.replaceState({}, '', url.toString());
  }

  // ── Article cache ─────────────────────────────────────────────────────────

  async loadAllArticles() {
    const base = window.location.pathname.split('/tagged')[0];
    const onTagged = window.location.pathname.includes('/tagged/');
    const collected = new Map();

    // Page count comes straight from Liquid's paginate.pages via a data
    // attribute — this theme's pagination renders as a "Show more" button
    // with no numbered page links, so parsing the markup for a count
    // doesn't work and silently caps the cache at page 1.
    const declaredPages = parseInt(this.dataset.totalPages || '1', 10);

    const collectFrom = (root) => {
      root.querySelectorAll('blog-results ul li[data-handle]').forEach((li) => {
        if (!collected.has(li.dataset.handle)) collected.set(li.dataset.handle, li);
      });
    };

    const fetchPage = (p) =>
      fetch(`${base}?page=${p}`)
        .then((r) => r.text())
        .then((html) => new DOMParser().parseFromString(html, 'text/html'));

    try {
      let maxPage;

      if (onTagged) {
        // Current page only shows one tag's articles — fetch the base
        // listing from page 1 to build the full cache. Page count still
        // has to come from a fresh fetch here since the tagged page's
        // own data-total-pages reflects the filtered set, not the total.
        const firstDoc = await fetchPage(1);
        collectFrom(firstDoc);
        const firstEl = firstDoc.querySelector('blog-recipes');
        maxPage = firstEl ? parseInt(firstEl.dataset.totalPages || '1', 10) : 1;
      } else {
        this.initialItems.forEach((li) => collected.set(li.dataset.handle, li));
        maxPage = declaredPages;
      }

      if (maxPage > 1) {
        const docs = await Promise.all(
          Array.from({ length: maxPage - 1 }, (_, i) => fetchPage(i + 2))
        );
        docs.forEach(collectFrom);
      }

      this.allItems = Array.from(collected.values());
      this.orderIndex = new Map();
      this.allItems.forEach((li, i) => this.orderIndex.set(li.dataset.handle, i));
    } catch (err) {
      console.error('Recipe cache error:', err);
      this.allItems = this.initialItems;
    }
  }

  // ── Filtering ─────────────────────────────────────────────────────────────

  /** An item's tags, already handleized by Liquid (so `dietary/vegan` arrives as `dietary-vegan`). */
  tagsOf(li) {
    return (li.dataset.tags || '').split(',').map((tag) => tag.trim()).filter(Boolean);
  }

  matchesTags(liTags, tags) {
    // OR: any one selected category is enough.
    return tags.some((selectedTag) =>
      liTags.some((articleTag) => {
        if (articleTag === selectedTag) return true;

        // Imported recipes may use hierarchical canonical tags such as
        // cat:dehydrated-meat/jerky/chicken-jerky, while the theme filter
        // is configured with the flat leaf tag chicken-jerky. Liquid's
        // handleize filter can expose that tag as
        // cat-dehydrated-meat-jerky-chicken-jerky, so support both forms.
        const canonicalTag = articleTag.replace(/^cat[:-]/, '');
        return canonicalTag.endsWith(`/${selectedTag}`) || canonicalTag.endsWith(`-${selectedTag}`);
      })
    );
  }

  matchesDiets(liTags, diets) {
    // AND: every ticked diet must be satisfied, which is what makes this
    // narrow the results instead of widening them.
    //
    // Both spellings are accepted because the raw tag is `dietary/vegan` but
    // the section writes data-tags through Liquid's handleize, which turns the
    // slash into a hyphen.
    return diets.every(
      (diet) => liTags.includes(`dietary-${diet}`) || liTags.includes(`dietary/${diet}`)
    );
  }

  applyFilters({ updateUrl = true } = {}) {
    const tags = this.getSelectedTags();
    const diets = this.getSelectedDiets();
    const q = this.searchQuery.trim().toLowerCase();
    const items = this.allItems || this.initialItems;

    let visible = null; // null = unfiltered, show the server-rendered page

    if (tags.length || diets.length || q) {
      visible = items.filter((li) => {
        const liTags = this.tagsOf(li);
        if (tags.length && !this.matchesTags(liTags, tags)) return false;
        if (diets.length && !this.matchesDiets(liTags, diets)) return false;
        if (!q) return true;
        const titleEl = li.querySelector('.uxf-recipe-card__title') || li.querySelector('h3, a');
        const title = (titleEl ? titleEl.textContent : '').toLowerCase();
        return title.includes(q);
      });
    }

    this.renderItems(visible);
    this.renderActiveFilters(tags, diets);
    if (updateUrl) this.updateURL(tags, diets, q);
  }

  renderItems(visible) {
    if (!this.grid) return;

    if (visible === null) {
      this.grid.replaceChildren(...this.initialItems);
      this.togglePagination(true);
      if (this.paginationMessage) this.paginationMessage.textContent = this.originalCountText;
      return;
    }

    this.togglePagination(false);

    if (!visible.length) {
      const q = this.searchQuery.trim();
      this.grid.innerHTML = `<li style="grid-column: 1 / -1; padding: 48px 0; text-align: center;">
        <p>No recipes found${q ? ` for "<strong>${this.escapeHtml(q)}</strong>"` : ''}.</p>
        <button type="button" class="btn btn--primary js-clear-filters" style="margin-top: 16px;">Clear filters</button>
      </li>`;
      this.updateCount(0);
      return;
    }

    this.grid.replaceChildren(...visible);
    this.updateCount(visible.length);
  }

  updateCount(count) {
    if (!this.paginationMessage) return;
    const total = (this.allItems || this.initialItems).length;
    this.paginationMessage.textContent = `Showing ${count} of ${total} recipes`;
  }

  togglePagination(show) {
    if (!this.results) return;
    this.results.querySelectorAll('.js-when-paginated-only, nav, .pagination').forEach((el) => {
      el.style.display = show ? '' : 'none';
    });
  }

  // ── Search ────────────────────────────────────────────────────────────────

  handleSearch(query) {
    this.searchQuery = query || '';
    // Keep both search inputs in sync
    document.querySelectorAll('input[name="recipe-query"]').forEach((input) => {
      if (input.value !== this.searchQuery) input.value = this.searchQuery;
    });
    this.applyFilters();
  }

  // ── Sort ──────────────────────────────────────────────────────────────────

  handleSort(value) {
    if (!this.grid) return;
    const items = Array.from(this.grid.querySelectorAll('li[data-handle]'));
    if (!items.length) return;

    const titleOf = (li) => {
      const el = li.querySelector('.uxf-recipe-card__title') || li.querySelector('h3, a');
      return (el ? el.textContent : '').trim();
    };

    items.sort((a, b) => {
      if (value === 'title-ascending') return titleOf(a).localeCompare(titleOf(b));
      if (value === 'title-descending') return titleOf(b).localeCompare(titleOf(a));
      // "Most Recent" / reset — restore original blog order
      return (this.orderIndex.get(a.dataset.handle) || 0) - (this.orderIndex.get(b.dataset.handle) || 0);
    });

    items.forEach((li) => this.grid.appendChild(li));
  }

  // ── Active filter chips ───────────────────────────────────────────────────

  renderActiveFilters(tags, diets) {
    const container = this.querySelector('.js-active-tags');
    if (!container) return;

    if (!tags.length && !diets.length) {
      container.innerHTML = '';
      container.hidden = true;
      return;
    }

    container.hidden = false;

    // Chips carry the full input name so removal does not have to guess which
    // kind of filter it came from — `vegetarian` could be either.
    const chip = (name, label, extraClass) => `<span class="recipe-filter-tag${extraClass}">
            ${this.escapeHtml(label)}
            <button type="button" class="js-remove-tag" data-name="${this.escapeHtml(name)}" aria-label="Remove ${this.escapeHtml(label)} filter">&times;</button>
          </span>`;

    // Ticking Fruit cascades to everything under it, so the selection is Fruit plus thirty of its
    // descendants. They all return the same recipes Fruit already does, and thirty-one chips read
    // as thirty-one decisions the customer did not make. The narrower ones are implied, so only
    // the outermost is shown.
    const outermost = tags.filter(
      (tag) => !tags.some((other) => other !== tag && tag.startsWith(`${other}-`)),
    );

    container.innerHTML =
      '<span class="recipe-filter-label">Filtered by:</span>' +
      outermost
        .map((tag) => chip(`tags=${tag}`, this.tagLabels.get(tag) || tag.replace(/-/g, ' '), ''))
        .join('') +
      diets
        .map((diet) => chip(`diet=${diet}`, DIET_LABELS[diet] || diet, ' recipe-filter-tag--diet'))
        .join('');
  }

  removeFilter(name) {
    if (!name) return;
    document.querySelectorAll(`input[name="${name}"]`).forEach((cb) => {
      cb.checked = false;
      cb.indeterminate = false;
      // Down as well as up. Ticking a category cascades to everything under it, so unticking it
      // has to release them too — otherwise removing the Fruit chip leaves thirty descendants
      // selected and the results do not change, which reads as a broken button.
      this.updateChildren(cb);
      this.updateAncestors(cb);
    });
    this.applyFilters();
  }

  clearAll() {
    document
      .querySelectorAll('#recipe-filters-desktop input[type="checkbox"], #recipe-filter-drawer input[type="checkbox"]')
      .forEach((cb) => {
        cb.checked = false;
        cb.indeterminate = false;
      });
    this.searchQuery = '';
    document.querySelectorAll('input[name="recipe-query"]').forEach((input) => {
      input.value = '';
    });
    this.applyFilters();
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
}

if (!customElements.get('blog-recipes')) {
  customElements.define('blog-recipes', BlogRecipes);
}
